import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CalculateRoundedIcon from "@mui/icons-material/CalculateRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";

const BRAND = {
  orange: "#F35A3A",
  orangeDark: "#DF4728",
  dark: "#19242F",
  green: "#2A9D67",
};

const PRESETS = [
  { key: "complementaria", label: "Complementaria", clients: 3, serviceCount: 1 },
  { key: "relevante", label: "En crecimiento", clients: 8, serviceCount: 2 },
  { key: "principal", label: "Dedicación principal", clients: 15, serviceCount: 3 },
];

function finiteNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function roundMoney(value) {
  return Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;
}

function normalizedCurrency(value) {
  return String(value || "ARS").trim().toUpperCase() || "ARS";
}

function percentLabel(value) {
  const parsed = finiteNumber(value);
  if (parsed === null) return "—";

  return `${new Intl.NumberFormat("es-AR", {
    maximumFractionDigits: 2,
  }).format(parsed)}%`;
}

function moneyLabel(value, currency) {
  const parsed = finiteNumber(value);
  if (parsed === null) return "—";

  try {
    return new Intl.NumberFormat("es-AR", {
      style: "currency",
      currency: normalizedCurrency(currency),
      maximumFractionDigits: 0,
    }).format(parsed);
  } catch {
    return `${new Intl.NumberFormat("es-AR", {
      maximumFractionDigits: 0,
    }).format(parsed)} ${normalizedCurrency(currency)}`;
  }
}

function validServices(services) {
  return (Array.isArray(services) ? services : [])
    .map((service) => ({
      ...service,
      code: String(service?.code || "").trim().toLowerCase(),
      name: String(service?.name || "").trim(),
      monthlyPrice: finiteNumber(service?.monthlyPrice),
      currency: normalizedCurrency(service?.currency),
      requiredServiceCodes: (Array.isArray(service?.requiredServiceCodes)
        ? service.requiredServiceCodes
        : [])
        .map((code) => String(code || "").trim().toLowerCase())
        .filter(Boolean),
    }))
    .filter(
      (service) =>
        service.code &&
        service.name &&
        service.monthlyPrice !== null &&
        service.monthlyPrice >= 0,
    );
}

function serviceMapFor(services) {
  return new Map(services.map((service) => [service.code, service]));
}

function expandDependencies(codes, servicesByCode) {
  const expanded = new Set(codes);
  const pending = [...expanded];

  while (pending.length) {
    const currentCode = pending.pop();
    const service = servicesByCode.get(currentCode);

    for (const requiredCode of service?.requiredServiceCodes || []) {
      if (!servicesByCode.has(requiredCode) || expanded.has(requiredCode)) continue;
      expanded.add(requiredCode);
      pending.push(requiredCode);
    }
  }

  return expanded;
}

function removeServiceAndDependents(code, selectedCodes, servicesByCode) {
  const remaining = new Set(selectedCodes);
  remaining.delete(code);

  let changed = true;
  while (changed) {
    changed = false;

    for (const selectedCode of [...remaining]) {
      const service = servicesByCode.get(selectedCode);
      if ((service?.requiredServiceCodes || []).some((requiredCode) => !remaining.has(requiredCode))) {
        remaining.delete(selectedCode);
        changed = true;
      }
    }
  }

  return remaining;
}

function preferredPresetCodes(services, serviceCount) {
  const servicesByCode = serviceMapFor(services);
  const paidServices = services
    .filter((service) => service.monthlyPrice > 0)
    .sort((left, right) => {
      if (left.recommended !== right.recommended) return left.recommended ? -1 : 1;
      if (left.monthlyPrice !== right.monthlyPrice) return left.monthlyPrice - right.monthlyPrice;
      return left.name.localeCompare(right.name, "es");
    });
  const candidates = paidServices.length ? paidServices : services;
  const initialCodes = candidates
    .slice(0, Math.max(1, serviceCount))
    .map((service) => service.code);

  return [...expandDependencies(initialCodes, servicesByCode)];
}

function totalsByCurrency(services, clients = 1) {
  const multiplier = Math.max(0, Number(clients || 0));
  const totals = new Map();

  for (const service of services) {
    const currency = normalizedCurrency(service.currency);
    totals.set(
      currency,
      roundMoney(Number(totals.get(currency) || 0) + Number(service.monthlyPrice || 0) * multiplier),
    );
  }

  return [...totals.entries()]
    .map(([currency, amount]) => ({ currency, amount }))
    .sort((left, right) => left.currency.localeCompare(right.currency));
}

function commissionTotals(totals, commissionPercent) {
  const parsedPercent = finiteNumber(commissionPercent);
  if (parsedPercent === null) return [];

  return totals.map(({ currency, amount }) => ({
    currency,
    amount: roundMoney((amount * parsedPercent) / 100),
  }));
}

function programLevels(program) {
  const levels = Array.isArray(program?.levelBehavior?.levels)
    ? program.levelBehavior.levels
    : [];

  return levels
    .map((level) => ({
      level: Math.max(1, Math.trunc(Number(level?.level || 0))),
      entitlementPercent: finiteNumber(level?.directCommissionPercent),
    }))
    .filter((level) => level.entitlementPercent !== null)
    .sort((left, right) => left.level - right.level);
}

function MoneyLines({ values, emphasis = false, emptyLabel = "Sin monto disponible" }) {
  if (!values.length) {
    return (
      <Typography sx={{ color: "rgba(25,36,47,.5)", fontWeight: 800, fontSize: ".88rem" }}>
        {emptyLabel}
      </Typography>
    );
  }

  return (
    <Stack spacing={0.15}>
      {values.map(({ currency, amount }) => (
        <Typography
          key={currency}
          sx={{
            color: emphasis ? BRAND.orange : BRAND.dark,
            fontWeight: 950,
            letterSpacing: "-.03em",
            lineHeight: 1.1,
            fontSize: emphasis ? { xs: "1.55rem", sm: "2.15rem" } : { xs: ".96rem", sm: "1.18rem" },
          }}
        >
          {moneyLabel(amount, currency)}
        </Typography>
      ))}
    </Stack>
  );
}

function DetailAccordion({ title, subtitle, children }) {
  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{
        borderRadius: "18px !important",
        border: "1px solid rgba(25,36,47,.09)",
        bgcolor: "#fff",
        overflow: "hidden",
        "&::before": { display: "none" },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreRoundedIcon />}
        sx={{
          px: { xs: 1.8, sm: 2.3 },
          minHeight: { xs: 48, sm: 58 },
          "& .MuiAccordionSummary-content": { my: { xs: .8, sm: 1.2 }, minWidth: 0 },
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ color: BRAND.dark, fontWeight: 950, fontSize: ".94rem" }}>
            {title}
          </Typography>
          {subtitle ? (
            <Typography sx={{ mt: 0.2, display: { xs: "none", sm: "block" }, color: "rgba(25,36,47,.54)", fontSize: ".75rem", lineHeight: 1.4 }}>
              {subtitle}
            </Typography>
          ) : null}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ px: { xs: 1.8, sm: 2.3 }, pt: 0, pb: { xs: 2, sm: 2.4 } }}>
        {children}
      </AccordionDetails>
    </Accordion>
  );
}

export default function SellerIncomeSimulator({ services: sourceServices, program, loading = false }) {
  const services = React.useMemo(() => validServices(sourceServices), [sourceServices]);
  const servicesByCode = React.useMemo(() => serviceMapFor(services), [services]);
  const catalogSignature = React.useMemo(
    () => services.map((service) => `${service.code}:${service.monthlyPrice}:${service.currency}`).join("|"),
    [services],
  );
  const [clients, setClients] = React.useState(PRESETS[0].clients);
  const [selectedCodes, setSelectedCodes] = React.useState([]);
  const [activePreset, setActivePreset] = React.useState(PRESETS[0].key);

  const applyPreset = React.useCallback(
    (preset) => {
      setClients(preset.clients);
      setSelectedCodes(preferredPresetCodes(services, preset.serviceCount));
      setActivePreset(preset.key);
    },
    [services],
  );

  React.useEffect(() => {
    if (!services.length) {
      setSelectedCodes([]);
      return;
    }

    setClients(PRESETS[0].clients);
    setSelectedCodes(preferredPresetCodes(services, PRESETS[0].serviceCount));
    setActivePreset(PRESETS[0].key);
  }, [catalogSignature, services]);

  const toggleService = (serviceCode) => {
    setActivePreset("");
    setSelectedCodes((currentCodes) => {
      const current = new Set(currentCodes);
      if (current.has(serviceCode)) {
        return [...removeServiceAndDependents(serviceCode, current, servicesByCode)];
      }

      current.add(serviceCode);
      return [...expandDependencies(current, servicesByCode)];
    });
  };

  const selectedServices = React.useMemo(
    () => services.filter((service) => selectedCodes.includes(service.code)),
    [selectedCodes, services],
  );
  const perBusiness = React.useMemo(() => totalsByCurrency(selectedServices, 1), [selectedServices]);
  const monthlyVolume = React.useMemo(
    () => totalsByCurrency(selectedServices, clients),
    [clients, selectedServices],
  );
  const levels = React.useMemo(() => programLevels(program), [program]);
  const directPercent = finiteNumber(program?.directCommissionPercent);
  const directCommission = React.useMemo(
    () => commissionTotals(monthlyVolume, directPercent),
    [directPercent, monthlyVolume],
  );
  const programIsActive = program?.status === "active" && program?.enabled === true;
  const hasPrices = services.length > 0;
  const hasSelection = selectedServices.length > 0;
  const visibleServices = selectedServices.slice(0, 3);
  const hiddenSelectedServices = Math.max(0, selectedServices.length - visibleServices.length);

  return (
    <Box id="vendedores-simulador" sx={{ mt: { xs: 4, sm: 6, md: 8 }, scrollMarginTop: 96 }}>
      <Box sx={{ maxWidth: 720 }}>
        <Typography sx={{ color: BRAND.orange, fontWeight: 900, letterSpacing: ".1em", textTransform: "uppercase", fontSize: ".74rem" }}>
          Simulador de ingresos
        </Typography>
        <Typography sx={{ mt: 0.8, color: BRAND.dark, fontWeight: 950, letterSpacing: "-.04em", fontSize: { xs: "1.5rem", sm: "1.9rem", md: "2.45rem" }, lineHeight: 1.08 }}>
          Probá una cartera con datos vigentes.
        </Typography>
        <Typography sx={{ mt: { xs: .7, sm: 1.05 }, color: "rgba(25,36,47,.62)", lineHeight: { xs: 1.42, sm: 1.55 }, fontSize: { xs: ".82rem", sm: ".96rem" } }}>
          Elegí un escenario rápido o personalizalo. La estimación usa los precios y porcentajes publicados por WebProfit.
        </Typography>
      </Box>

      {loading ? (
        <Paper elevation={0} sx={{ mt: 2.6, p: 2.5, borderRadius: 3.5, border: "1px solid rgba(25,36,47,.09)", bgcolor: "#fff" }}>
          <Stack direction="row" alignItems="center" spacing={1.2}>
            <CircularProgress size={22} sx={{ color: BRAND.orange }} />
            <Typography sx={{ color: "rgba(25,36,47,.62)", fontWeight: 800, fontSize: ".88rem" }}>
              Cargando catálogo y política vigentes…
            </Typography>
          </Stack>
        </Paper>
      ) : !hasPrices ? (
        <Alert severity="info" sx={{ mt: 2.6, borderRadius: 3.2 }}>
          El simulador se habilita cuando WebProfit publica al menos un servicio con precio vigente.
        </Alert>
      ) : (
        <Stack spacing={{ xs: .9, sm: 1.4 }} sx={{ mt: { xs: 1.8, sm: 2.6 } }}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 1.25, sm: 2.4, md: 3 },
              borderRadius: 4,
              border: "1px solid rgba(243,90,58,.18)",
              bgcolor: "#fff",
              boxShadow: "0 16px 38px rgba(25,36,47,.045)",
            }}
          >
            <Typography sx={{ color: "rgba(25,36,47,.52)", fontWeight: 900, fontSize: ".7rem", letterSpacing: ".06em", textTransform: "uppercase" }}>
              Escenario rápido
            </Typography>
            <Box
              sx={{
                mt: 1,
                display: "grid",
                gridTemplateColumns: { xs: "repeat(3,minmax(0,1fr))", sm: "repeat(3,minmax(0,1fr))" },
                gap: { xs: .45, sm: .8 },
              }}
            >
              {PRESETS.map((preset) => {
                const isActive = activePreset === preset.key;
                return (
                  <Button
                    key={preset.key}
                    onClick={() => applyPreset(preset)}
                    variant={isActive ? "contained" : "outlined"}
                    sx={{
                      justifyContent: "flex-start",
                      minHeight: { xs: 43, sm: 48 },
                      px: { xs: .65, sm: 1.4 },
                      borderRadius: 2.6,
                      textTransform: "none",
                      fontWeight: 900,
                      bgcolor: isActive ? BRAND.dark : "#fff",
                      color: isActive ? "#fff" : BRAND.dark,
                      borderColor: isActive ? BRAND.dark : "rgba(25,36,47,.13)",
                      boxShadow: "none",
                      "&:hover": {
                        bgcolor: isActive ? BRAND.dark : "rgba(25,36,47,.03)",
                        borderColor: BRAND.dark,
                        boxShadow: "none",
                      },
                    }}
                  >
                    <Box sx={{ textAlign: "left", minWidth: 0 }}>
                      <Typography component="span" sx={{ display: "block", fontWeight: 950, fontSize: { xs: ".72rem", sm: ".82rem" }, lineHeight: 1.18 }}>
                        {preset.label}
                      </Typography>
                      <Typography component="span" sx={{ display: { xs: "none", sm: "block" }, mt: 0.2, opacity: 0.72, fontWeight: 750, fontSize: ".68rem" }}>
                        {preset.clients} clientes · {preset.serviceCount} {preset.serviceCount === 1 ? "servicio" : "servicios"} base
                      </Typography>
                    </Box>
                  </Button>
                );
              })}
            </Box>

            <Box
              sx={{
                mt: { xs: 1, sm: 1.5 },
                p: { xs: 1.2, sm: 2.2 },
                borderRadius: 3.2,
                bgcolor: "#FFF8F5",
                border: "1px solid rgba(243,90,58,.14)",
              }}
            >
              <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={{ xs: 1.1, sm: 2, md: 3 }}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Stack direction="row" alignItems="center" spacing={0.8}>
                    <GroupsRoundedIcon sx={{ color: BRAND.orange, fontSize: 20 }} />
                    <Typography sx={{ color: BRAND.dark, fontWeight: 950, fontSize: ".92rem" }}>
                      {clients} {clients === 1 ? "cliente" : "clientes"} · {selectedServices.length} {selectedServices.length === 1 ? "servicio" : "servicios"}
                    </Typography>
                  </Stack>
                  <Stack direction="row" flexWrap="wrap" useFlexGap gap={0.55} sx={{ mt: 1 }}>
                    {visibleServices.map((service) => (
                      <Chip
                        key={service.code}
                        size="small"
                        label={service.name}
                        sx={{ bgcolor: "#fff", fontWeight: 800, border: "1px solid rgba(25,36,47,.08)" }}
                      />
                    ))}
                    {hiddenSelectedServices > 0 ? (
                      <Chip size="small" label={`+${hiddenSelectedServices}`} sx={{ fontWeight: 850 }} />
                    ) : null}
                    {!hasSelection ? <Chip size="small" label="Sin servicios" /> : null}
                  </Stack>
                </Box>

                <Box sx={{ minWidth: { md: 235 } }}>
                  <Typography sx={{ color: "rgba(25,36,47,.52)", fontSize: ".68rem", fontWeight: 900, textTransform: "uppercase", letterSpacing: ".055em" }}>
                    Comisión directa estimada
                  </Typography>
                  <Box sx={{ mt: 0.45 }}>
                    <MoneyLines
                      values={hasSelection && programIsActive ? directCommission : []}
                      emphasis
                      emptyLabel="No disponible"
                    />
                  </Box>
                  <Typography sx={{ mt: 0.45, color: "rgba(25,36,47,.58)", fontSize: ".73rem", lineHeight: 1.4 }}>
                    {programIsActive && directPercent !== null
                      ? `${percentLabel(directPercent)} vigente sobre pagos elegibles.`
                      : "La política pública no permite estimarla ahora."}
                  </Typography>
                </Box>
              </Stack>

              <Typography
                sx={{
                  mt: 1,
                  display: { xs: "block", sm: "none" },
                  color: "rgba(25,36,47,.58)",
                  fontSize: ".72rem",
                  lineHeight: 1.4,
                }}
              >
                Supuesto: la misma combinación se mantiene en {clients} {clients === 1 ? "proyecto" : "proyectos"}.
              </Typography>

              <Box
                sx={{
                  mt: 1.6,
                  pt: 1.4,
                  borderTop: "1px solid rgba(25,36,47,.08)",
                  display: { xs: "none", sm: "grid" },
                  gridTemplateColumns: "repeat(3,minmax(0,1fr))",
                  gap: 1,
                }}
              >
                <Box>
                  <Typography sx={{ color: "rgba(25,36,47,.5)", fontSize: ".68rem", fontWeight: 850 }}>POR NEGOCIO</Typography>
                  <Box sx={{ mt: 0.35 }}><MoneyLines values={hasSelection ? perBusiness : []} /></Box>
                </Box>
                <Box>
                  <Typography sx={{ color: "rgba(25,36,47,.5)", fontSize: ".68rem", fontWeight: 850 }}>VOLUMEN CARTERA</Typography>
                  <Box sx={{ mt: 0.35 }}><MoneyLines values={hasSelection ? monthlyVolume : []} /></Box>
                </Box>
                <Box>
                  <Typography sx={{ color: "rgba(25,36,47,.5)", fontSize: ".68rem", fontWeight: 850 }}>SUPUESTO</Typography>
                  <Typography sx={{ mt: 0.35, color: BRAND.dark, fontWeight: 850, fontSize: ".8rem", lineHeight: 1.35 }}>
                    Misma combinación de servicios en {clients} {clients === 1 ? "proyecto" : "proyectos"}.
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Paper>

          <DetailAccordion
            title="Personalizar escenario"
            subtitle="Cambiá cantidad de clientes o servicios sólo si necesitás salir de los ejemplos rápidos."
          >
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "220px minmax(0,1fr)" }, gap: { xs: 1.4, md: 2.5 } }}>
              <Box>
                <Typography sx={{ color: BRAND.dark, fontWeight: 900, fontSize: ".82rem" }}>Cantidad de clientes</Typography>
                <TextField
                  type="number"
                  value={clients}
                  onChange={(event) => {
                    const next = Math.min(100, Math.max(1, Math.trunc(Number(event.target.value) || 1)));
                    setClients(next);
                    setActivePreset("");
                  }}
                  inputProps={{ min: 1, max: 100, step: 1, inputMode: "numeric", "aria-label": "Cantidad de clientes" }}
                  fullWidth
                  size="small"
                  sx={{ mt: 0.8, "& .MuiOutlinedInput-root": { borderRadius: 2.5 } }}
                />
                <Typography sx={{ mt: 0.65, color: "rgba(25,36,47,.5)", fontSize: ".7rem", lineHeight: 1.4 }}>
                  Entre 1 y 100 proyectos con la misma selección.
                </Typography>
              </Box>

              <Box>
                <Stack direction="row" alignItems="center" spacing={0.7}>
                  <CalculateRoundedIcon sx={{ color: BRAND.orange, fontSize: 19 }} />
                  <Typography sx={{ color: BRAND.dark, fontWeight: 900, fontSize: ".82rem" }}>Servicios por proyecto</Typography>
                </Stack>
                <Box sx={{ mt: 0.8, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2,minmax(0,1fr))" }, gap: { xs: .55, sm: .75 } }}>
                  {services.map((service) => {
                    const checked = selectedCodes.includes(service.code);
                    const requiredBy = selectedServices.filter((selectedService) =>
                      selectedService.requiredServiceCodes.includes(service.code),
                    );

                    return (
                      <Paper
                        key={service.code}
                        elevation={0}
                        sx={{
                          p: { xs: .7, sm: .85 },
                          borderRadius: 2.5,
                          border: checked ? "1px solid rgba(243,90,58,.32)" : "1px solid rgba(25,36,47,.08)",
                          bgcolor: checked ? "#FFF8F5" : "#fff",
                        }}
                      >
                        <FormControlLabel
                          sx={{ m: 0, width: "100%", alignItems: "flex-start" }}
                          control={(
                            <Checkbox
                              size="small"
                              checked={checked}
                              onChange={() => toggleService(service.code)}
                              sx={{ pt: 0.1, color: "rgba(25,36,47,.34)", "&.Mui-checked": { color: BRAND.orange } }}
                            />
                          )}
                          label={(
                            <Box sx={{ minWidth: 0, pt: 0.1 }}>
                              <Typography sx={{ color: BRAND.dark, fontWeight: 900, fontSize: ".82rem", lineHeight: 1.25 }}>
                                {service.name}
                              </Typography>
                              <Typography sx={{ mt: 0.2, color: BRAND.orangeDark, fontWeight: 900, fontSize: ".74rem" }}>
                                {moneyLabel(service.monthlyPrice, service.currency)} / mes
                              </Typography>
                              {requiredBy.length ? (
                                <Typography sx={{ mt: 0.2, color: "rgba(25,36,47,.48)", fontSize: ".65rem", lineHeight: 1.3 }}>
                                  Requerido por {requiredBy.map((item) => item.name).join(", ")}
                                </Typography>
                              ) : null}
                            </Box>
                          )}
                        />
                      </Paper>
                    );
                  })}
                </Box>
              </Box>
            </Box>
          </DetailAccordion>

          <DetailAccordion
            title="Ver detalle del cálculo"
            subtitle="Mensualidad, volumen y porcentaje usados en esta estimación."
          >
            <Stack spacing={1.5}>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3,minmax(0,1fr))" }, gap: { xs: .65, sm: 1 } }}>
                <Paper elevation={0} sx={{ p: 1.4, borderRadius: 2.7, bgcolor: "#F7F8FA" }}>
                  <Typography sx={{ color: "rgba(25,36,47,.5)", fontSize: ".67rem", fontWeight: 900 }}>1. MENSUALIDAD</Typography>
                  <Box sx={{ mt: 0.45 }}><MoneyLines values={hasSelection ? perBusiness : []} /></Box>
                  <Typography sx={{ mt: 0.4, color: "rgba(25,36,47,.54)", fontSize: ".69rem" }}>Suma de servicios elegidos.</Typography>
                </Paper>
                <Paper elevation={0} sx={{ p: 1.4, borderRadius: 2.7, bgcolor: "#F7F8FA" }}>
                  <Typography sx={{ color: "rgba(25,36,47,.5)", fontSize: ".67rem", fontWeight: 900 }}>2. VOLUMEN</Typography>
                  <Box sx={{ mt: 0.45 }}><MoneyLines values={hasSelection ? monthlyVolume : []} /></Box>
                  <Typography sx={{ mt: 0.4, color: "rgba(25,36,47,.54)", fontSize: ".69rem" }}>Mensualidad × {clients} clientes.</Typography>
                </Paper>
                <Paper elevation={0} sx={{ p: 1.4, borderRadius: 2.7, bgcolor: "#F7F8FA" }}>
                  <Typography sx={{ color: "rgba(25,36,47,.5)", fontSize: ".67rem", fontWeight: 900 }}>3. COMISIÓN DIRECTA</Typography>
                  <Typography sx={{ mt: 0.45, color: BRAND.dark, fontWeight: 950, fontSize: "1.1rem" }}>
                    {programIsActive && directPercent !== null ? percentLabel(directPercent) : "No disponible"}
                  </Typography>
                  <Typography sx={{ mt: 0.4, color: "rgba(25,36,47,.54)", fontSize: ".69rem" }}>Se aplica al volumen elegible.</Typography>
                </Paper>
              </Box>

              <Box>
                <Typography sx={{ color: BRAND.dark, fontWeight: 900, fontSize: ".79rem" }}>Precios incluidos</Typography>
                <Stack spacing={0.55} sx={{ mt: 0.7 }}>
                  {selectedServices.map((service) => (
                    <Stack key={service.code} direction="row" justifyContent="space-between" spacing={1.2} sx={{ py: 0.45, borderBottom: "1px solid rgba(25,36,47,.06)" }}>
                      <Typography sx={{ color: "rgba(25,36,47,.68)", fontSize: ".76rem", minWidth: 0 }}>{service.name}</Typography>
                      <Typography sx={{ color: BRAND.dark, fontWeight: 900, fontSize: ".76rem", whiteSpace: "nowrap" }}>
                        {moneyLabel(service.monthlyPrice, service.currency)} / mes
                      </Typography>
                    </Stack>
                  ))}
                  {!selectedServices.length ? (
                    <Typography sx={{ color: "rgba(25,36,47,.5)", fontSize: ".76rem" }}>Seleccioná al menos un servicio.</Typography>
                  ) : null}
                </Stack>
              </Box>
            </Stack>
          </DetailAccordion>

          <DetailAccordion
            title="Ver política por niveles"
            subtitle={programIsActive
              ? `${levels.length} ${levels.length === 1 ? "nivel publicado" : "niveles publicados"}; los superiores reciben diferencias entre derechos.`
              : "La política está pausada o incompleta."}
          >
            {programIsActive && levels.length && hasSelection ? (
              <Stack spacing={1}>
                <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={0.8}>
                  <Typography sx={{ color: "rgba(25,36,47,.62)", fontSize: ".76rem", lineHeight: 1.5, maxWidth: 620 }}>
                    Cada nivel muestra el derecho estimado sobre esta misma cartera. El referente superior recibe sólo la diferencia correspondiente, no porcentajes acumulados sin límite.
                  </Typography>
                  <Chip
                    size="small"
                    label={`Tope red: ${percentLabel(program?.levelBehavior?.maximumTotalCommissionPercent)}`}
                    sx={{ alignSelf: { xs: "flex-start", sm: "center" }, bgcolor: "rgba(42,157,103,.1)", color: "#157A4A", fontWeight: 900 }}
                  />
                </Stack>

                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2,minmax(0,1fr))", lg: "repeat(3,minmax(0,1fr))" }, gap: 0.8 }}>
                  {levels.map((level, index) => {
                    const previousEntitlement = index > 0 ? levels[index - 1].entitlementPercent : null;
                    const overridePercent = previousEntitlement === null
                      ? null
                      : Math.max(0, roundMoney(previousEntitlement - level.entitlementPercent));
                    const sellerAmount = commissionTotals(monthlyVolume, level.entitlementPercent);

                    return (
                      <Paper key={level.level} elevation={0} sx={{ p: 1.35, borderRadius: 2.7, bgcolor: "#F4FBF7", border: "1px solid rgba(42,157,103,.12)" }}>
                        <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="baseline">
                          <Typography sx={{ color: BRAND.dark, fontWeight: 950, fontSize: ".86rem" }}>Nivel {level.level}</Typography>
                          <Typography sx={{ color: BRAND.green, fontWeight: 950, fontSize: ".82rem" }}>{percentLabel(level.entitlementPercent)}</Typography>
                        </Stack>
                        <Box sx={{ mt: 0.65 }}><MoneyLines values={sellerAmount} /></Box>
                        <Typography sx={{ mt: 0.55, color: "rgba(25,36,47,.52)", fontSize: ".68rem", lineHeight: 1.4 }}>
                          {overridePercent === null
                            ? "Venta directa propia."
                            : `Diferencia del superior inmediato: ${percentLabel(overridePercent)}.`}
                        </Typography>
                      </Paper>
                    );
                  })}
                </Box>
              </Stack>
            ) : (
              <Typography sx={{ color: "rgba(25,36,47,.58)", fontSize: ".8rem" }}>
                No hay niveles calculables con la política y selección actuales.
              </Typography>
            )}
          </DetailAccordion>

          <Alert
            severity="warning"
            icon={<PaymentsRoundedIcon fontSize="inherit" />}
            sx={{ borderRadius: 3, alignItems: "flex-start", "& .MuiAlert-message": { py: 0.1 } }}
          >
            <Typography sx={{ fontWeight: 900, fontSize: ".82rem" }}>
              Es una estimación, no una promesa de ingresos.
            </Typography>
            <Typography sx={{ mt: 0.2, display: { xs: "none", sm: "block" }, fontSize: ".74rem", lineHeight: 1.45 }}>
              La comisión real depende de pagos válidos, confirmados y elegibles; devoluciones, cancelaciones y cambios de política pueden modificarla.
            </Typography>
          </Alert>
        </Stack>
      )}
    </Box>
  );
}
